import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Job, ProcessingStep } from '../types';
import { getJob, checkRetryEligibility } from '../services/api';
import { format } from 'date-fns';
import { ReprocessPanel } from './ReprocessPanel';
import { RetryPanel } from './RetryPanel';

/**
 * TimelineModal Component
 *
 * Displays a modal overlay with a job's processing timeline.
 * Shows header info (filename, press, status, duration) and a vertical
 * timeline of processingSteps with status icons, redirect indicators,
 * and collapsible error details.
 *
 * Requirements: 1.1, 1.2, 1.3, 3.1–3.7, 4.1, 4.2, 8.1–8.3
 */

export interface TimelineModalProps {
  jobId: string;
  onClose: () => void;
}

/** Map step status to a visual icon element */
export function getStatusIcon(status: ProcessingStep['status']): React.ReactElement {
  switch (status) {
    case 'completed':
      return (
        <span data-testid="icon-completed" className="text-green-500 text-lg" aria-label="Completed">
          ✓
        </span>
      );
    case 'in_progress':
      return (
        <span
          data-testid="icon-in_progress"
          className="inline-block h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
          aria-label="In progress"
        />
      );
    case 'paused':
      return (
        <span data-testid="icon-paused" className="text-orange-500 text-lg" aria-label="Paused">
          ⏸
        </span>
      );
    case 'reprocessing':
      return (
        <span
          data-testid="icon-reprocessing"
          className="inline-block h-4 w-4 bg-blue-500 rounded-full animate-pulse"
          aria-label="Reprocessing"
        />
      );
    case 'failed':
      return (
        <span data-testid="icon-failed" className="text-red-500 text-lg" aria-label="Failed">
          ✗
        </span>
      );
    default:
      return (
        <span
          data-testid="icon-pending"
          className="inline-block h-4 w-4 bg-gray-300 rounded-full"
          aria-label="Pending"
        />
      );
  }
}

/** Format a unix-ms timestamp for display */
function formatTimestamp(ts: number): string {
  return format(new Date(ts), 'yyyy-MM-dd HH:mm:ss');
}

/** Compute human-readable duration between two timestamps (ms) */
export function formatDuration(startTime: number, endTime?: number): string {
  if (!endTime) return 'In progress';
  const diffMs = endTime - startTime;
  if (diffMs < 0) return '-';
  const totalSeconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

/** Render a redirect indicator for steps with redirect info */
function RedirectIndicator({ redirectFrom, redirectTo }: { redirectFrom: string; redirectTo: string }) {
  return (
    <span data-testid="redirect-indicator" className="inline-flex items-center text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded ml-2">
      {redirectFrom} → {redirectTo}
    </span>
  );
}

/** Collapsible error details for failed steps */
function ErrorDetails({ errorMessage, errorDetails }: { errorMessage?: string; errorDetails?: string }) {
  const [expanded, setExpanded] = useState(false);

  if (!errorMessage && !errorDetails) return null;

  return (
    <div className="mt-1 text-sm">
      {errorMessage && <p className="text-red-600">{errorMessage}</p>}
      {errorDetails && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-gray-500 underline mt-1"
            data-testid="toggle-error-details"
          >
            {expanded ? 'Hide technical details' : 'Show technical details'}
          </button>
          {expanded && (
            <pre data-testid="error-details" className="mt-1 text-xs bg-gray-100 p-2 rounded overflow-x-auto text-gray-700">
              {errorDetails}
            </pre>
          )}
        </>
      )}
    </div>
  );
}

export const TimelineModal: React.FC<TimelineModalProps> = ({ jobId, onClose }) => {
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryEligible, setRetryEligible] = useState<boolean>(false);
  const [retryEligibilityError, setRetryEligibilityError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  /** Fetch job data */
  const fetchJob = useCallback(async () => {
    try {
      const data = await getJob(jobId);
      setJob(data);
      setError(null);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job');
      return null;
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  /** Stop polling */
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  /** Start polling if job is processing/reprocessing */
  const startPolling = useCallback(
    (currentJob: Job) => {
      stopPolling();
      if (currentJob.status === 'processing' || currentJob.status === 'reprocessing') {
        pollingRef.current = setInterval(async () => {
          const updated = await fetchJob();
          if (updated && (updated.status === 'completed' || updated.status === 'failed')) {
            stopPolling();
          }
        }, 5000);
      }
    },
    [fetchJob, stopPolling]
  );

  /** Initial fetch */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchJob();
      if (!cancelled && data) {
        startPolling(data);
      }
    })();
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [fetchJob, startPolling, stopPolling]);

  /** Escape key handler */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  /** Check retry eligibility when job is failed */
  useEffect(() => {
    if (!job || job.status !== 'failed') {
      setRetryEligible(false);
      setRetryEligibilityError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await checkRetryEligibility(job.jobId);
        if (!cancelled) {
          setRetryEligible(result.eligible);
          setRetryEligibilityError(result.eligible ? null : (result.reason || null));
        }
      } catch {
        if (!cancelled) {
          setRetryEligible(false);
          setRetryEligibilityError('Unable to check retry eligibility');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [job?.jobId, job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Click outside handler */
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  return (
    <div
      data-testid="timeline-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Job Timeline"
    >
      <div
        ref={modalContentRef}
        data-testid="timeline-modal-content"
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto m-4"
      >
        {/* Close button */}
        <div className="flex justify-end p-2">
          <button
            onClick={onClose}
            data-testid="close-button"
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {loading && (
          <div className="flex justify-center items-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" role="status" aria-label="Loading" />
          </div>
        )}

        {error && (
          <div className="p-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={() => { setLoading(true); fetchJob(); }} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && job && (
          <div className="px-6 pb-6">
            {/* Header */}
            <div data-testid="timeline-header" className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">{job.filename}</h2>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Press:</span>{' '}
                  <span data-testid="header-press">{job.site}{job.press}</span>
                </div>
                <div>
                  <span className="font-medium">Status:</span>{' '}
                  <span data-testid="header-status" className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                    job.status === 'completed' ? 'bg-green-100 text-green-800' :
                    job.status === 'failed' ? 'bg-red-100 text-red-800' :
                    job.status === 'paused' ? 'bg-orange-100 text-orange-800' :
                    job.status === 'reprocessing' ? 'bg-blue-100 text-blue-800' :
                    job.status === 'expired' ? 'bg-purple-100 text-purple-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {job.status}
                  </span>
                </div>
                <div>
                  <span className="font-medium">Duration:</span>{' '}
                  <span data-testid="header-duration">{formatDuration(job.startTime, job.endTime)}</span>
                </div>
              </div>
            </div>

            {/* Timeline */}
            {job.processingSteps && job.processingSteps.length > 0 ? (
              <div data-testid="timeline-steps" className="relative">
                {/* Vertical line */}
                <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200" />

                {[...job.processingSteps].sort((a, b) => a.timestamp - b.timestamp).map((step, index) => (
                  <div key={index} data-testid={`timeline-step-${index}`} className="relative flex items-start mb-4 pl-10">
                    {/* Icon */}
                    <div className="absolute left-0 flex items-center justify-center w-7 h-7">
                      {getStatusIcon(step.status)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap">
                        <span className="font-medium text-sm text-gray-900">{step.stage}</span>
                        {step.redirectFrom && step.redirectTo && (
                          <RedirectIndicator redirectFrom={step.redirectFrom} redirectTo={step.redirectTo} />
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{formatTimestamp(step.timestamp)}</p>

                      {step.paths && step.paths.length > 0 && (
                        <div className="text-xs text-gray-500 mt-0.5 truncate" title={step.paths.join(', ')}>
                          {step.paths.join(', ')}
                        </div>
                      )}

                      {step.stage === 'RETRY' && step.retryBy && (
                        <div data-testid={`retry-info-${index}`} className="text-xs text-blue-600 mt-0.5">
                          Retried by: {step.retryBy}
                        </div>
                      )}

                      {step.stage === 'EXPIRED' && step.paths && step.paths.length > 0 && (
                        <div data-testid={`expired-archive-path-${index}`} className="text-xs text-purple-600 mt-0.5">
                          Archived to: {step.paths[0]}
                        </div>
                      )}

                      {step.extractedFiles && step.extractedFiles.length > 0 && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          Extracted: {step.extractedFiles.join(', ')}
                        </div>
                      )}

                      {step.status === 'failed' && (
                        <ErrorDetails errorMessage={step.errorMessage} errorDetails={step.errorDetails} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div data-testid="legacy-fallback" className="text-center text-gray-500 py-8">
                Timeline data not available for this job
              </div>
            )}

            {/* Reprocess Panel for paused jobs */}
            {job.status === 'paused' && (
              <ReprocessPanel
                jobId={job.jobId}
                originalPress={`${job.site}${parseInt(job.press, 10)}`}
                onReprocessed={async () => {
                  const updated = await fetchJob();
                  if (updated) startPolling(updated);
                }}
              />
            )}

            {/* Retry Panel for failed jobs with eligible source files */}
            {job.status === 'failed' && retryEligible && (
              <RetryPanel
                jobId={job.jobId}
                originalPress={`${job.site}${parseInt(job.press, 10)}`}
                onRetried={async () => {
                  const updated = await fetchJob();
                  if (updated) startPolling(updated);
                }}
              />
            )}

            {/* Message when failed but not eligible for retry */}
            {job.status === 'failed' && !retryEligible && retryEligibilityError && (
              <div data-testid="retry-ineligible-message" className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                {retryEligibilityError}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineModal;
